/**
 * Moi et mon entreprise — la matière première de tout le reste.
 *
 * Deux personnes se rencontrent, pas deux fiches : on demande donc l'humain
 * (rôle, forces, ce qu'on aime) autant que la société (services, client idéal,
 * chiffre d'affaires). Le pitch est proposé par l'IA depuis le site web, mais
 * n'entre dans les rapprochements qu'une fois VALIDÉ : l'IA propose, la
 * personne décide.
 */
import { useEffect, useState } from 'react';
import { Check, Plus, Search, Settings2, Sparkles, X } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { ChoixPhoto } from '../../composants/ChoixPhoto.tsx';
import { Badge, Btn, Card, Modal, PageTitle, SectionLabel } from '../../admin/ui.tsx';
import {
  FORCES,
  OBJECTIFS,
  RESEAUX_FIXES,
  SPORTS_A_COTE,
  SPORTS_FREQUENTS,
  SUJETS,
  TAILLES,
  TRANCHES_AGE,
  TRANCHES_CA,
  reseauDe,
  type Espace,
  type Lien,
  type Membre,
  type Offre,
  type Sport,
} from './types.ts';

export function MonEntreprise({
  espace,
  onChange,
  sansTitre = false,
}: {
  espace: Espace;
  onChange: () => Promise<void>;
  /** Monté dans l'application d'événement, sous son propre titre. */
  sansTitre?: boolean;
}) {
  const p = espace.profil;
  const [f, setF] = useState({
    prenom: espace.identite.prenom ?? '',
    nom: espace.identite.nom ?? '',
    telephone: p?.telephone ?? '',
    entreprise: p?.entreprise ?? '',
    fonction: p?.fonction ?? '',
    secteur: p?.secteur ?? '',
    tailleEntreprise: p?.taille_entreprise ?? '',
    caAnnuel: p?.ca_annuel ?? '',
    services: p?.services ?? '',
    clientIdeal: p?.client_ideal ?? '',
    partenaireIdeal: p?.partenaire_ideal ?? '',
    pourQuoiFaire: p?.pour_quoi_faire ?? '',
    recherche: p?.recherche ?? '',
    bio: p?.bio ?? '',
    situation: p?.situation ?? '',
    origineGeographique: p?.origine_geographique ?? '',
    trancheAge: p?.tranche_age ?? '',
    siteWeb: p?.site_web ?? '',
    linkedin: p?.linkedin ?? '',
    photoUrl: p?.photo_url ?? '',
    logoUrl: p?.logo_url ?? '',
    numeroTva: p?.numero_tva ?? '',
  });
  const [forces, setForces] = useState<string[]>(p?.forces ?? []);
  const [sujets, setSujets] = useState<string[]>(p?.sujets_conversation ?? []);
  const [objectifs, setObjectifs] = useState<string[]>(p?.objectifs ?? []);
  const [interets, setInterets] = useState<string[]>(p?.interets ?? []);
  const [liens, setLiens] = useState<Lien[]>(p?.liens ?? []);
  const [offres, setOffres] = useState<Offre[]>(() => depuisTexte(p?.offres ?? [], p?.services ?? null));
  const [demandes, setDemandes] = useState<Offre[]>(() => depuisTexte(p?.demandes ?? [], p?.recherche ?? null));
  const [sports, setSports] = useState<Sport[]>(p?.sports ?? []);
  const [pseudo, setPseudo] = useState(p?.pseudo ?? '');
  const [pseudoEtat, setPseudoEtat] = useState<{ disponible: boolean; raison?: string } | null>(null);
  const [interetSaisi, setInteretSaisi] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const poser = (cle: keyof typeof f, v: string) => setF((x) => ({ ...x, [cle]: v }));

  useEffect(() => {
    const propre = pseudo.trim().toLowerCase();
    if (!propre || propre === (p?.pseudo ?? '')) {
      setPseudoEtat(null);
      return;
    }
    const minuterie = setTimeout(async () => {
      const r = await appelerApi<{ disponible: boolean; raison?: string }>(`/api/compte/pseudo-disponible?pseudo=${encodeURIComponent(propre)}`);
      setPseudoEtat(r.donnees ?? null);
    }, 300);
    return () => clearTimeout(minuterie);
  }, [pseudo, p?.pseudo]);

  const enregistrer = async () => {
    setEnCours(true);
    setMessage(null);
    const r = await envoyer(
      '/api/compte/moi',
      {
        prenom: f.prenom.trim() || undefined,
        nom: f.nom.trim() || undefined,
        telephone: f.telephone.trim() || null,
        entreprise: f.entreprise.trim() || null,
        fonction: f.fonction.trim() || null,
        secteur: f.secteur.trim() || null,
        tailleEntreprise: f.tailleEntreprise || null,
        caAnnuel: f.caAnnuel || null,
        clientIdeal: f.clientIdeal.trim() || null,
        partenaireIdeal: f.partenaireIdeal.trim() || null,
        pourQuoiFaire: f.pourQuoiFaire.trim() || null,
        bio: f.bio.trim() || null,
        situation: f.situation.trim() || null,
        origineGeographique: f.origineGeographique.trim() || null,
        trancheAge: f.trancheAge || null,
        siteWeb: f.siteWeb.trim() || null,
        linkedin: f.linkedin.trim() || null,
        photoUrl: f.photoUrl.trim() || null,
        logoUrl: f.logoUrl.trim() || null,
        forces,
        sujetsConversation: sujets,
        objectifs,
        interets,
        liens: liens.filter((l) => l.url.trim()),
        offres,
        demandes,
        sports,
        pseudo: pseudo.trim() || null,
        numeroTva: f.numeroTva.trim() || null,
      },
      'PUT',
    );
    setEnCours(false);
    setMessage(r.erreur ?? 'Enregistré ✓');
    if (!r.erreur) await onChange();
  };

  return (
    <>
      {!sansTitre && (
      <PageTitle
        title={
          <>
            Moi et mon <em className="text-match-gradient font-serif italic font-medium">entreprise</em>
          </>
        }
        subtitle="Tout part d’ici : ce que vous remplissez pré-remplit chaque événement, nourrit les mises en relation et prépare vos rendez-vous. Les deux champs décisifs restent « ce que je propose » et « ce que je cherche »."
      />
      )}

      <Pitch espace={espace} onChange={onChange} />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Vous</SectionLabel>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Champ label="Prénom" value={f.prenom} onChange={(v) => poser('prenom', v)} />
            <Champ label="Nom" value={f.nom} onChange={(v) => poser('nom', v)} />
            <Champ label="Votre rôle" value={f.fonction} onChange={(v) => poser('fonction', v)} placeholder="Fondatrice, gérant…" />
            <Champ label="Téléphone" value={f.telephone} onChange={(v) => poser('telephone', v)} />
          </div>
          <p className="mt-3 text-[13px] text-fumee">{espace.identite.email}</p>

          <div className="mt-5 border-t border-graphite pt-4">
            <span id="adresse" className="block scroll-mt-6" />
            <SectionLabel>Votre adresse EventMatch</SectionLabel>
            <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
              Votre page de rendez-vous, à mettre en signature d’e-mail. Le même lien inscrit et parraine.
            </p>
            <div className="mt-3 flex items-center gap-0 rounded-[8px] border border-graphite">
              <span className="shrink-0 pl-3 text-[14px] text-fumee">eventmatch.pro/</span>
              <input
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="tomwart"
                maxLength={30}
                className="field-input !min-h-[42px] !border-0 !py-2 !pl-0 !text-sm"
              />
            </div>
            {pseudoEtat && (
              <p className="mt-1.5 text-[12px]" style={{ color: pseudoEtat.disponible ? undefined : 'var(--em-erreur)' }}>
                {pseudoEtat.disponible ? <span className="text-blanc">Disponible ✓</span> : (pseudoEtat.raison ?? 'Déjà pris')}
              </p>
            )}
            {pseudo && (
              <p className="mt-1.5 text-[12px] text-fumee">
                Inscription directe : <span className="text-blanc">eventmatch.pro/{pseudo}/join</span>
              </p>
            )}
          </div>

          <div className="mt-5 border-t border-graphite pt-4">
            <SectionLabel>Vos forces</SectionLabel>
            <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
              Deux profils complémentaires — un tech et un commercial — font une association. On
              s’en sert pour vous proposer ce qui vous manque.
            </p>
            <Pastilles options={FORCES} valeurs={forces} onChange={setForces} />
          </div>
        </Card>

        <Card>
          <SectionLabel>L’entreprise</SectionLabel>
          <div className="mt-4 flex flex-col gap-4">
            <Champ label="Nom de l’entreprise" value={f.entreprise} onChange={(v) => poser('entreprise', v)} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Champ label="Secteur" value={f.secteur} onChange={(v) => poser('secteur', v)} placeholder="Artisanat & construction" />
              <Champ label="Numéro de TVA (facultatif)" value={f.numeroTva} onChange={(v) => poser('numeroTva', v)} placeholder="BE 0123.456.789" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Liste label="Taille" value={f.tailleEntreprise} onChange={(v) => poser('tailleEntreprise', v)} options={TAILLES} />
              <Liste label="Chiffre d’affaires annuel" value={f.caAnnuel} onChange={(v) => poser('caAnnuel', v)} options={TRANCHES_CA} />
            </div>
            <p className="text-[12px] leading-relaxed text-fumee">
              La tranche de chiffre d’affaires est visible des autres comptes : elle situe l’ordre
              de grandeur, jamais le montant exact.
            </p>
            <div className="border-t border-graphite pt-4">
              <SectionLabel>Logo de l’entreprise</SectionLabel>
              <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
                C’est lui qui apparaît dans les annonces d’événements et sur les murs — distinct de votre photo.
              </p>
              <div className="mt-3">
                <ChoixPhoto valeur={f.logoUrl || null} onChange={(v) => poser('logoUrl', v ?? '')} nom={f.entreprise} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <SectionLabel>Ce que je propose</SectionLabel>
          <p className="mt-2 text-[13px] leading-relaxed text-fumee">Un service par élément. Cliquez sur la molette pour le préciser.</p>
          <Nuage valeurs={offres} onChange={setOffres} placeholder="Menuiserie sur mesure" exempleDetail="Agencement de bureaux et de magasins, pose comprise." />
        </Card>
        <Card>
          <span id="recherche" className="block scroll-mt-6" />
          <SectionLabel>Mon client idéal</SectionLabel>
          <p className="mt-2 text-[13px] leading-relaxed text-fumee">À qui vendez-vous le mieux ? C’est ce qui décide devant quelle audience on vous place — et ce qui s’affiche sur le Seek Wall des événements.</p>
          <Zone value={f.clientIdeal} onChange={(v) => poser('clientIdeal', v)} rows={3} placeholder="Ex. : architectes et promoteurs qui livrent 5 à 20 chantiers par an en province de Liège." />
          <div className="mt-4 border-t border-graphite pt-4">
            <SectionLabel>Mon partenaire idéal</SectionLabel>
            <p className="mt-2 text-[13px] leading-relaxed text-fumee">Pas un client : quelqu’un avec qui avancer.</p>
            <Zone value={f.partenaireIdeal} onChange={(v) => poser('partenaireIdeal', v)} rows={2} placeholder="Ex. : un bureau d’études qui travaille déjà avec des promoteurs." />
          </div>
          <div className="mt-4 border-t border-graphite pt-4">
            <SectionLabel>Pour quoi faire ensemble</SectionLabel>
            <p className="mt-2 text-[13px] leading-relaxed text-fumee">L’intention, en une phrase. C’est elle qui déclenche les bonnes conversations.</p>
            <Zone value={f.pourQuoiFaire} onChange={(v) => poser('pourQuoiFaire', v)} rows={2} placeholder="Ex. : répondre ensemble aux marchés publics de rénovation." />
          </div>
        </Card>
        <Card>
          <SectionLabel>Ce que je cherche</SectionLabel>
          <p className="mt-2 text-[13px] leading-relaxed text-fumee">Le nuage qui déclenche les meilleures mises en relation. Précisez chaque élément.</p>
          <Nuage valeurs={demandes} onChange={setDemandes} placeholder="Un comptable" exempleDetail="Qui connaît la construction et les chantiers en province de Liège." />
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Mes objectifs cette année</SectionLabel>
          <Pastilles options={OBJECTIFS} valeurs={objectifs} onChange={setObjectifs} />
        </Card>
        <Card>
          <SectionLabel>Hors travail</SectionLabel>
          <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
            Une passion partagée ouvre une conversation mieux qu’un secteur d’activité. C’est ce
            qui sert de brise-glace dans les suggestions.
          </p>
          <Pastilles options={SUJETS} valeurs={sujets} onChange={setSujets} />
          <ChoixSports sports={sports} onChange={setSports} actif={sujets.includes('Sport') || sports.length > 0} />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Champ label="D’où venez-vous" value={f.origineGeographique} onChange={(v) => poser('origineGeographique', v)} placeholder="Liège, Ardenne, Portugal…" />
            <Liste label="Tranche d’âge" value={f.trancheAge} onChange={(v) => poser('trancheAge', v)} options={TRANCHES_AGE} />
          </div>
          <div className="mt-4">
            <Champ label="Votre situation, si vous voulez" value={f.situation} onChange={(v) => poser('situation', v)} placeholder="Deux enfants, installé à Liège depuis 2019" />
          </div>
          <p className="mt-2 text-[12px] leading-relaxed text-fumee">
            Tout ce bloc est facultatif et visible des autres comptes. Il sert aux sujets de
            conversation, à rien d’autre.
          </p>
        </Card>
      </div>

      <Card className="mt-4">
        <SectionLabel>Centres d’intérêt professionnels</SectionLabel>
        <div className="mt-3 flex flex-wrap gap-2">
          {interets.map((i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-graphite px-3 py-1.5 text-[13px] text-blanc">
              {i}
              <button onClick={() => setInterets((l) => l.filter((x) => x !== i))} aria-label={`Retirer ${i}`} className="text-fumee hover:text-blanc">
                <X size={13} strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
        <input
          value={interetSaisi}
          onChange={(e) => setInteretSaisi(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ',') return;
            e.preventDefault();
            const v = interetSaisi.trim();
            if (v && !interets.some((x) => x.toLowerCase() === v.toLowerCase())) setInterets((l) => [...l, v]);
            setInteretSaisi('');
          }}
          placeholder="Ajouter puis Entrée"
          className="field-input mt-3 !min-h-[40px] !py-2 !text-sm"
        />
      </Card>

      <Card className="mt-4">
        <SectionLabel>Présentation et liens</SectionLabel>
        <Zone value={f.bio} onChange={(v) => poser('bio', v)} placeholder="En deux phrases, pour qu’on vous situe d’un coup d’œil." rows={2} />
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Champ label="Site web" value={f.siteWeb} onChange={(v) => poser('siteWeb', v)} placeholder="https://…" />
          <Champ label="LinkedIn" value={f.linkedin} onChange={(v) => poser('linkedin', v)} placeholder="https://…" />
          <div>
            <span className="label-caps block text-fumee">Votre photo</span>
            <div className="mt-2">
              <ChoixPhoto valeur={f.photoUrl || null} onChange={(v) => poser('photoUrl', v ?? '')} nom={`${f.prenom} ${f.nom}`} />
            </div>
          </div>
        </div>
        <div className="mt-5 border-t border-graphite pt-4">
          <SectionLabel>Vos réseaux</SectionLabel>
          <p className="mt-1.5 text-[13px] text-fumee">Un lien par réseau ; chaque clic reçu est compté et nourrit votre influence.</p>
          <ReseauxSociaux liens={liens} onChange={setLiens} />
        </div>
      </Card>

      {message && (
        <p className="mt-4 text-[13px] text-fumee" role="status">
          {message}
        </p>
      )}
      <div className="mt-6">
        <Btn variant="primary" disabled={enCours} onClick={() => void enregistrer()}>
          {enCours ? '…' : 'Enregistrer'}
        </Btn>
      </div>

      <MotDePasse espace={espace} onChange={onChange} />
      <Equipe espace={espace} onChange={onChange} />
    </>
  );
}

/* ————————————————— Mot de passe ————————————————— */

function MotDePasse({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const enregistrer = async () => {
    setMessage(null);
    const r = await envoyer('/api/compte/mot-de-passe', { actuel: espace.a_mot_de_passe ? actuel : null, nouveau });
    if (r.erreur) return setMessage(r.erreur);
    setActuel('');
    setNouveau('');
    setMessage('Mot de passe enregistré ✓');
    await onChange();
  };
  return (
    <Card className="mt-4">
      <SectionLabel>{espace.a_mot_de_passe ? 'Changer mon mot de passe' : 'Poser un mot de passe'}</SectionLabel>
      <p className="mt-1.5 text-[13px] leading-relaxed text-fumee">
        {espace.a_mot_de_passe
          ? 'Vous vous connectez avec votre e-mail et ce mot de passe. Le lien par e-mail reste possible si vous l’oubliez.'
          : 'Votre compte s’ouvre aujourd’hui par lien e-mail. Un mot de passe vous permettra d’entrer directement.'}
      </p>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        {espace.a_mot_de_passe && <Champ label="Mot de passe actuel" value={actuel} onChange={setActuel} type="password" />}
        <Champ label="Nouveau mot de passe (8 caractères min.)" value={nouveau} onChange={setNouveau} type="password" />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Btn onClick={() => void enregistrer()} disabled={nouveau.length < 8}>Enregistrer</Btn>
        {message && <span className="text-[13px] text-fumee">{message}</span>}
      </div>
    </Card>
  );
}

/* ————————————————— Le pitch, proposé puis validé ————————————————— */

function Pitch({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const p = espace.profil;
  const [texte, setTexte] = useState(p?.pitch_brouillon ?? p?.pitch ?? '');
  const [enCours, setEnCours] = useState<'generer' | 'valider' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const generer = async () => {
    setEnCours('generer');
    setMessage(null);
    const r = await envoyer<{ brouillon: string | null }>('/api/compte/pitch/generer', {});
    setEnCours(null);
    if (r.donnees?.brouillon) {
      setTexte(r.donnees.brouillon);
      setMessage('Proposition prête — relisez-la avant de valider.');
    } else {
      setMessage(
        espace.ia_disponible
          ? 'Rien à proposer : renseignez votre site web ou ce que vous proposez, puis réessayez.'
          : 'L’IA n’est pas encore configurée sur la plateforme — écrivez votre pitch à la main, il fera le même travail.',
      );
    }
    await onChange();
  };

  const valider = async () => {
    setEnCours('valider');
    const r = await envoyer('/api/compte/pitch/valider', { texte: texte.trim() });
    setEnCours(null);
    setMessage(r.erreur ?? 'Pitch validé ✓ — il sert maintenant aux rapprochements.');
    if (!r.erreur) await onChange();
  };

  const enAttente = Boolean(p?.pitch_brouillon);
  const valide = Boolean(p?.pitch_valide_le) && !enAttente;

  return (
    <Card className={enAttente ? 'border border-graphite' : ''}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionLabel>Le pitch de votre entreprise</SectionLabel>
        {valide && <Badge filled>Validé</Badge>}
        {enAttente && <Badge>À relire</Badge>}
      </div>
      <p className="mt-2 max-w-[680px] text-[13px] leading-relaxed text-fumee">
        C’est le texte qui vous présente aux autres entrepreneurs et qui sert à calculer les
        rapprochements. L’IA peut le rédiger depuis votre site web — elle propose, vous décidez :
        rien n’est utilisé tant que vous n’avez pas validé.
      </p>
      <textarea
        rows={5}
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        placeholder="Quatre à six phrases : ce que vous faites, pour qui, ce qui vous distingue, ce que vous cherchez en ce moment."
        className="field-input mt-3 !min-h-0 !py-2 !text-sm"
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Btn disabled={enCours !== null} onClick={() => void generer()}>
          <Sparkles size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          {enCours === 'generer' ? '…' : p?.site_web ? 'Proposer depuis mon site' : 'Proposer un pitch'}
        </Btn>
        <Btn variant="primary" disabled={enCours !== null || !texte.trim()} onClick={() => void valider()}>
          {enCours === 'valider' ? '…' : 'Valider ce pitch'}
        </Btn>
        {message && <span className="text-[13px] text-fumee">{message}</span>}
      </div>
    </Card>
  );
}

/* ————————————————— L'équipe ————————————————— */

const MEMBRE_VIDE = { prenom: '', nom: '', fonction: '', email: '', telephone: '', forces: [] as string[], liens: [] as Lien[] };

function Equipe({ espace, onChange }: { espace: Espace; onChange: () => Promise<void> }) {
  const [edition, setEdition] = useState<{ id: string | null; v: typeof MEMBRE_VIDE } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const enregistrer = async () => {
    if (!edition) return;
    const corps = {
      prenom: edition.v.prenom,
      nom: edition.v.nom || null,
      fonction: edition.v.fonction || null,
      email: edition.v.email || null,
      telephone: edition.v.telephone || null,
      forces: edition.v.forces,
      liens: edition.v.liens.filter((l) => l.url.trim()),
    };
    const r = edition.id
      ? await envoyer(`/api/compte/equipe/${edition.id}`, corps, 'PUT')
      : await envoyer('/api/compte/equipe', corps);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setErreur(null);
    setEdition(null);
    await onChange();
  };

  const supprimer = async (id: string) => {
    await envoyer(`/api/compte/equipe/${id}`, {}, 'DELETE');
    await onChange();
  };

  return (
    <section className="mt-10">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-light text-blanc">Mon équipe</h2>
          <p className="mt-1 max-w-[640px] text-[13px] leading-relaxed text-fumee">
            Pour présenter la bonne personne plutôt que vous-même : quand une entreprise rencontrée
            cherche un profil que vous avez en interne, vous la mettez en relation directement.
          </p>
        </div>
        <Btn variant="primary" onClick={() => setEdition({ id: null, v: { ...MEMBRE_VIDE } })}>
          Ajouter un membre
        </Btn>
      </div>

      {espace.equipe.length === 0 ? (
        <Card>
          <p className="text-[14px] leading-relaxed text-fumee">
            Personne pour l’instant. Ajoutez vos associés et vos commerciaux : chacun avec ses
            forces et ses réseaux.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {espace.equipe.map((m: Membre) => (
            <Card key={m.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-[15px] text-blanc">{[m.prenom, m.nom].filter(Boolean).join(' ')}</p>
                  {m.fonction && <p className="truncate text-[13px] text-fumee">{m.fonction}</p>}
                </div>
                <button
                  onClick={() =>
                    setEdition({
                      id: m.id,
                      v: {
                        prenom: m.prenom,
                        nom: m.nom ?? '',
                        fonction: m.fonction ?? '',
                        email: m.email ?? '',
                        telephone: m.telephone ?? '',
                        forces: m.forces ?? [],
                        liens: m.liens ?? [],
                      },
                    })
                  }
                  className="shrink-0 text-[13px] text-fumee hover:text-blanc"
                >
                  Modifier
                </button>
              </div>
              {m.forces.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.forces.map((x) => (
                    <span key={x} className="rounded-full border border-graphite px-2.5 py-1 text-[12px] text-fumee">
                      {x}
                    </span>
                  ))}
                </div>
              )}
              {m.liens.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-[12px]">
                  {m.liens.map((l) => (
                    <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="text-fumee underline hover:text-blanc">
                      {l.libelle || reseauDe(l.url)}
                    </a>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {edition && (
        <Modal title={edition.id ? 'Modifier le membre' : 'Ajouter un membre'} onClose={() => setEdition(null)}>
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Champ label="Prénom" value={edition.v.prenom} onChange={(v) => setEdition({ ...edition, v: { ...edition.v, prenom: v } })} />
              <Champ label="Nom" value={edition.v.nom} onChange={(v) => setEdition({ ...edition, v: { ...edition.v, nom: v } })} />
            </div>
            <Champ label="Fonction" value={edition.v.fonction} onChange={(v) => setEdition({ ...edition, v: { ...edition.v, fonction: v } })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Champ label="E-mail" value={edition.v.email} onChange={(v) => setEdition({ ...edition, v: { ...edition.v, email: v } })} />
              <Champ label="Téléphone" value={edition.v.telephone} onChange={(v) => setEdition({ ...edition, v: { ...edition.v, telephone: v } })} />
            </div>
            <div>
              <SectionLabel>Ses forces</SectionLabel>
              <Pastilles options={FORCES} valeurs={edition.v.forces} onChange={(x) => setEdition({ ...edition, v: { ...edition.v, forces: x } })} />
            </div>
            <div>
              <SectionLabel>Ses réseaux</SectionLabel>
              <div className="mt-2 flex flex-col gap-2">
                {edition.v.liens.map((l, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={l.libelle}
                      onChange={(e) =>
                        setEdition({ ...edition, v: { ...edition.v, liens: edition.v.liens.map((y, j) => (j === i ? { ...y, libelle: e.target.value } : y)) } })
                      }
                      placeholder={l.url ? reseauDe(l.url) : 'LinkedIn'}
                      className="field-input !min-h-[38px] !w-auto min-w-[110px] !py-1.5 !text-sm"
                    />
                    <input
                      value={l.url}
                      onChange={(e) =>
                        setEdition({ ...edition, v: { ...edition.v, liens: edition.v.liens.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)) } })
                      }
                      placeholder="https://…"
                      className="field-input !min-h-[38px] flex-1 !py-1.5 !text-sm"
                    />
                    <button
                      onClick={() => setEdition({ ...edition, v: { ...edition.v, liens: edition.v.liens.filter((_, j) => j !== i) } })}
                      aria-label="Supprimer"
                      className="text-fumee hover:text-blanc"
                    >
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setEdition({ ...edition, v: { ...edition.v, liens: [...edition.v.liens, { libelle: '', url: '' }] } })}
                className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-fumee hover:text-blanc"
              >
                <Plus size={14} strokeWidth={1.5} /> Ajouter un réseau
              </button>
            </div>
            {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
            <div className="flex flex-wrap justify-between gap-3">
              {edition.id ? (
                <button onClick={() => void supprimer(edition.id!).then(() => setEdition(null))} className="text-[13px] text-fumee underline hover:text-blanc">
                  Retirer de l’équipe
                </button>
              ) : (
                <span />
              )}
              <span className="flex gap-3">
                <Btn onClick={() => setEdition(null)}>Annuler</Btn>
                <Btn variant="primary" onClick={() => void enregistrer()}>Enregistrer</Btn>
              </span>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

/* ————————————————— Champs ————————————————— */

/* ————————————————— Le nuage d'éléments précisables ————————————————— */

/** Un texte libre hérité devient des éléments : une phrase, un élément. */
function depuisTexte(existants: Offre[], texte: string | null): Offre[] {
  if (existants.length > 0 || !texte?.trim()) return existants;
  return texte
    .split(/[.;\n]+|,\s(?=[A-ZÀ-Ü])/)
    .map((x) => x.trim())
    .filter((x) => x.length >= 3)
    .slice(0, 12)
    .map((x) => ({ nom: x.slice(0, 80), detail: null }));
}

function Nuage({ valeurs, onChange, placeholder, exempleDetail }: { valeurs: Offre[]; onChange: (v: Offre[]) => void; placeholder: string; exempleDetail: string }) {
  const [saisie, setSaisie] = useState('');
  const [edition, setEdition] = useState<number | null>(null);
  const ajouter = () => {
    const v = saisie.trim();
    if (!v || valeurs.some((x) => x.nom.toLowerCase() === v.toLowerCase())) return;
    onChange([...valeurs, { nom: v.slice(0, 80), detail: null }]);
    setSaisie('');
  };
  const e = edition !== null ? valeurs[edition] : null;
  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2">
        {valeurs.map((o, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-graphite py-1.5 pl-3 pr-1.5 text-[13px] text-blanc">
            <span title={o.detail ?? undefined}>{o.nom}</span>
            {o.detail && <span className="h-1.5 w-1.5 rounded-full bg-blanc/70" title="Précisé" />}
            <button onClick={() => setEdition(i)} aria-label={`Préciser ${o.nom}`} className="rounded-full p-1 text-fumee hover:text-blanc">
              <Settings2 size={13} strokeWidth={1.5} />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          value={saisie}
          onChange={(ev) => setSaisie(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ',') {
              ev.preventDefault();
              ajouter();
            }
          }}
          placeholder={placeholder}
          className="field-input !min-h-[40px] flex-1 !py-2 !text-sm"
        />
        <Btn onClick={ajouter}><Plus size={13} strokeWidth={1.5} className="mr-1 inline" />Ajouter</Btn>
      </div>
      {e && edition !== null && (
        <Modal title="Préciser" onClose={() => setEdition(null)}>
          <div className="flex flex-col gap-4">
            <Champ label="Intitulé" value={e.nom} onChange={(v) => onChange(valeurs.map((x, j) => (j === edition ? { ...x, nom: v } : x)))} />
            <label className="block">
              <span className="label-caps block text-fumee">Précision</span>
              <textarea
                rows={3}
                value={e.detail ?? ''}
                onChange={(ev) => onChange(valeurs.map((x, j) => (j === edition ? { ...x, detail: ev.target.value || null } : x)))}
                placeholder={exempleDetail}
                className="field-input mt-2 !min-h-0 !py-2 !text-sm"
              />
            </label>
            <div className="flex items-center justify-between gap-3">
              <Btn variant="primary" onClick={() => setEdition(null)}>Terminé</Btn>
              <Btn onClick={() => { onChange(valeurs.filter((_, j) => j !== edition)); setEdition(null); }}>Supprimer</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ————————————————— Les sports, avec niveau et côté ————————————————— */

function ChoixSports({ sports, onChange, actif }: { sports: Sport[]; onChange: (v: Sport[]) => void; actif: boolean }) {
  const [ouvert, setOuvert] = useState(false);
  const [q, setQ] = useState('');
  const [precision, setPrecision] = useState<number | null>(null);
  if (!actif) return null;
  const filtre = q.trim().toLowerCase();
  const liste = SPORTS_FREQUENTS.filter((x) => !filtre || x.toLowerCase().includes(filtre));
  const choisi = (nom: string) => sports.some((s) => s.nom.toLowerCase() === nom.toLowerCase());
  const basculer = (nom: string) =>
    onChange(choisi(nom) ? sports.filter((s) => s.nom.toLowerCase() !== nom.toLowerCase()) : [...sports, { nom, niveau: null, cote: null }]);
  const p = precision !== null ? sports[precision] : null;

  return (
    <div className="mt-3 rounded-[8px] border border-graphite p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-fumee">Vos sports :</span>
        {sports.map((s, i) => (
          <span key={s.nom} className="inline-flex items-center gap-1.5 rounded-full border border-graphite py-1 pl-3 pr-1 text-[13px] text-blanc">
            {s.nom}
            {(s.niveau || s.cote) && <span className="text-fumee">· {[s.niveau, s.cote === 'gauche' ? 'gauche' : s.cote === 'droite' ? 'droite' : null].filter(Boolean).join(' · ')}</span>}
            <button onClick={() => setPrecision(i)} aria-label={`Préciser ${s.nom}`} className="rounded-full p-1 text-fumee hover:text-blanc">
              <Settings2 size={13} strokeWidth={1.5} />
            </button>
          </span>
        ))}
        <button onClick={() => setOuvert(true)} className="inline-flex items-center gap-1.5 rounded-full border border-graphite px-3 py-1.5 text-[13px] text-fumee hover:text-blanc">
          <Settings2 size={13} strokeWidth={1.5} /> {sports.length === 0 ? 'Choisir mes sports' : 'Modifier'}
        </button>
      </div>

      {ouvert && (
        <Modal title="Quels sports ?" onClose={() => setOuvert(false)}>
          <div className="relative">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-fumee" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un sport" className="field-input !min-h-[40px] !py-2 !pl-9 !text-sm" autoFocus />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {liste.map((nom) => (
              <button key={nom} onClick={() => basculer(nom)} className={`rounded-full border border-graphite px-3 py-1.5 text-[13px] transition-colors ${choisi(nom) ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}>
                {choisi(nom) && <Check size={11} strokeWidth={2} className="mr-1 inline" />}
                {nom}
              </button>
            ))}
            {filtre && !SPORTS_FREQUENTS.some((x) => x.toLowerCase() === filtre) && (
              <button onClick={() => { basculer(q.trim()); setQ(''); }} className="rounded-full border border-dashed border-graphite px-3 py-1.5 text-[13px] text-fumee hover:text-blanc">
                <Plus size={11} strokeWidth={1.5} className="mr-1 inline" /> Ajouter « {q.trim()} »
              </button>
            )}
          </div>
          <Btn variant="primary" className="mt-5" onClick={() => setOuvert(false)}>Terminé</Btn>
        </Modal>
      )}

      {p && precision !== null && (
        <Modal title={p.nom} onClose={() => setPrecision(null)}>
          <div className="flex flex-col gap-4">
            <Champ
              label="Votre niveau ou classement"
              value={p.niveau ?? ''}
              onChange={(v) => onChange(sports.map((x, j) => (j === precision ? { ...x, niveau: v || null } : x)))}
              placeholder={SPORTS_A_COTE.includes(p.nom) ? 'Non classé, C30.4, B-15…' : 'Débutant, loisir, compétition…'}
            />
            {SPORTS_A_COTE.includes(p.nom) && (
              <div>
                <span className="label-caps block text-fumee">En double, vous jouez</span>
                <div className="mt-2 flex gap-2">
                  {(['gauche', 'droite'] as const).map((c) => (
                    <Btn key={c} variant={p.cote === c ? 'primary' : 'ghost'} onClick={() => onChange(sports.map((x, j) => (j === precision ? { ...x, cote: x.cote === c ? null : c } : x)))}>
                      {c === 'gauche' ? 'À gauche' : 'À droite'}
                    </Btn>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <Btn variant="primary" onClick={() => setPrecision(null)}>Terminé</Btn>
              <Btn onClick={() => { onChange(sports.filter((_, j) => j !== precision)); setPrecision(null); }}>Retirer</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ————————————————— Les réseaux, icône par icône ————————————————— */

const ICONES: Record<string, string> = {
  LinkedIn: 'M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7c.6-1 1.9-2 3.9-2 4.1 0 4.8 2.7 4.8 6.2V21h-4v-5.3c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V21h-4V9Z',
  Facebook: 'M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.2H7.3V14h2.8v8h3.4Z',
  Instagram: 'M12 7.3a4.7 4.7 0 1 0 0 9.4 4.7 4.7 0 0 0 0-9.4Zm0 7.7a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm5.9-7.9a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0ZM12 2c-2.7 0-3 0-4.1.1-2.9.1-4.7 1.9-4.8 4.8C3 8 3 8.3 3 11v2c0 2.7 0 3 .1 4.1.1 2.9 1.9 4.7 4.8 4.8C9 22 9.3 22 12 22s3 0 4.1-.1c2.9-.1 4.7-1.9 4.8-4.8.1-1.1.1-1.4.1-4.1v-2c0-2.7 0-3-.1-4.1-.1-2.9-1.9-4.7-4.8-4.8C15 2 14.7 2 12 2Zm0 1.8c2.7 0 3 0 4 .1 2 .1 3 1 3.1 3.1.1 1 .1 1.3.1 4v2c0 2.7 0 3-.1 4-.1 2-1 3-3.1 3.1-1 .1-1.3.1-4 .1s-3 0-4-.1c-2-.1-3-1-3.1-3.1-.1-1-.1-1.3-.1-4v-2c0-2.7 0-3 .1-4C5 5 6 4 8 3.9c1-.1 1.3-.1 4-.1Z',
  X: 'M17.5 3h3l-7 8 8.2 10h-6.4l-5-6.6L4.6 21h-3l7.5-8.6L1.3 3h6.6l4.5 6L17.5 3Zm-1 16.2h1.7L6.8 4.7H5L16.5 19.2Z',
  TikTok: 'M16.6 5.8A4.3 4.3 0 0 1 15.5 3h-3.2v12.4a2.6 2.6 0 1 1-1.8-2.5V9.6a5.9 5.9 0 1 0 5 5.8V9a7.4 7.4 0 0 0 4.3 1.4V7.2a4.3 4.3 0 0 1-3.2-1.4Z',
  YouTube: 'M21.6 7.2c-.2-.9-.9-1.6-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4c-.9.2-1.6.9-1.8 1.8C2 8.8 2 12 2 12s0 3.2.4 4.8c.2.9.9 1.6 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4c.9-.2 1.6-.9 1.8-1.8.4-1.6.4-4.8.4-4.8s0-3.2-.4-4.8ZM10 15.5v-7l6 3.5-6 3.5Z',
};

function IconeReseau({ nom }: { nom: string }) {
  const d = ICONES[nom];
  if (!d) return <span className="flex h-5 w-5 items-center justify-center rounded-full border border-graphite text-[10px] text-fumee">{nom[0]}</span>;
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="shrink-0">
      <path d={d} fill="currentColor" />
    </svg>
  );
}

function ReseauxSociaux({ liens, onChange }: { liens: Lien[]; onChange: (l: Lien[]) => void }) {
  // Chaque réseau fixe prend le premier lien qui lui ressemble ; le reste est « autre ».
  const estDe = (url: string, r: (typeof RESEAUX_FIXES)[number]) => r.domaines.some((d) => url.toLowerCase().includes(d));
  const indexFixe = (r: (typeof RESEAUX_FIXES)[number]) => liens.findIndex((l) => estDe(l.url, r));
  const autres = liens.map((l, i) => ({ l, i })).filter(({ l }) => !RESEAUX_FIXES.some((r) => estDe(l.url, r)) && (l.url.trim() || l.libelle.trim() || true) && !RESEAUX_FIXES.some((r) => estDe(l.url, r)));
  const autresIdx = autres.filter(({ l }) => !(l.url === '' && RESEAUX_FIXES.some((r) => l.libelle === r.nom)));

  const poserFixe = (r: (typeof RESEAUX_FIXES)[number], url: string) => {
    const i = indexFixe(r);
    if (i >= 0) {
      onChange(url.trim() ? liens.map((l, j) => (j === i ? { ...l, url, libelle: r.nom } : l)) : liens.filter((_, j) => j !== i));
    } else if (url.trim()) {
      onChange([...liens, { libelle: r.nom, url }]);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2">
      {RESEAUX_FIXES.map((r) => {
        const i = indexFixe(r);
        return (
          <label key={r.nom} className="flex items-center gap-3">
            <span className="flex w-28 shrink-0 items-center gap-2 text-[13px] text-blanc">
              <IconeReseau nom={r.nom} /> {r.nom}
            </span>
            <input
              value={i >= 0 ? liens[i]!.url : ''}
              onChange={(e) => poserFixe(r, e.target.value)}
              placeholder={r.placeholder}
              className="field-input !min-h-[38px] flex-1 !py-1.5 !text-sm"
            />
          </label>
        );
      })}
      {autresIdx.map(({ l, i }) => (
        <div key={i} className="flex items-center gap-3">
          <span className="flex w-28 shrink-0 items-center gap-2 text-[13px] text-blanc">
            <IconeReseau nom={l.url ? reseauDe(l.url) : '?'} />
            <input
              value={l.libelle}
              onChange={(e) => onChange(liens.map((y, j) => (j === i ? { ...y, libelle: e.target.value } : y)))}
              placeholder="Nom"
              className="field-input !min-h-[38px] !w-20 !py-1.5 !text-sm"
            />
          </span>
          <input
            value={l.url}
            onChange={(e) => onChange(liens.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)))}
            placeholder="https://…"
            className="field-input !min-h-[38px] flex-1 !py-1.5 !text-sm"
          />
          <button onClick={() => onChange(liens.filter((_, j) => j !== i))} aria-label="Supprimer" className="text-fumee hover:text-blanc">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
      ))}
      {liens.length < 12 && (
        <button onClick={() => onChange([...liens, { libelle: '', url: '' }])} className="mt-1 inline-flex items-center gap-1.5 self-start text-[13px] text-fumee hover:text-blanc">
          <Plus size={14} strokeWidth={1.5} /> Autre réseau
        </button>
      )}
    </div>
  );
}

export function Champ({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="label-caps block text-fumee">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="field-input mt-2 !min-h-[42px] !py-2 !text-sm" />
    </label>
  );
}

export function Liste({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { valeur: string; texte: string }[] }) {
  return (
    <label className="block">
      <span className="label-caps block text-fumee">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="field-input mt-2 !min-h-[42px] !py-2 !text-sm">
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur} className="bg-charbon">
            {o.texte}
          </option>
        ))}
      </select>
    </label>
  );
}

function Zone({ value, onChange, placeholder, rows = 4 }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="field-input mt-3 !min-h-0 !py-2 !text-sm" />;
}

export function Pastilles({ options, valeurs, onChange }: { options: string[]; valeurs: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {options.map((o) => {
        const actif = valeurs.includes(o);
        return (
          <button
            key={o}
            onClick={() => onChange(actif ? valeurs.filter((x) => x !== o) : [...valeurs, o])}
            className={`rounded-full border border-graphite px-3 py-1.5 text-[13px] transition-colors ${actif ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}
